import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Chip, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, IconButton, CircularProgress, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SupplierService } from '../../services/supplier.service';
import { loadSuppliersSafely } from '../../utils/supplierAccess';
import { SupplierItemService } from '../../services/supplierItem.service';
import { ItemService } from '../../services/item.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { PoApprovalService } from '../../services/poApproval.service';
import { AuthService } from '../../services/auth.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { SupplierItem } from '../../interfaces/procurement/supplierItem.interface';
import { Item } from '../../interfaces/item.interface';
import { PurchaseOrderCreateDTO } from '../../interfaces/procurement/purchaseOrder.interface';
import { IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import { RaisableRequisition } from '../../interfaces/procurement/poApproval.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';
// ---- G5a ----
import DocumentCurrencyFields from '../common/DocumentCurrencyFields';
import { formatFc, isForeignCurrency, toastSaved } from '../../utils/fcDocuments';
import { BackToBackLcService } from '../../services/backToBackLc.service';
import { BackToBackLc } from '../../interfaces/commercial.interface';
// ---- end G5a ----

interface LineDraft {
  itemId: string;
  quantity: number;
  unitPrice: number;
  isFromCatalogue: boolean;
}

const emptyLine = (): LineDraft => ({ itemId: '', quantity: 1, unitPrice: 0, isFromCatalogue: false });

export const PurchaseOrderCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillSupplierId = searchParams.get('supplierId') || '';
  const prefillItemId = searchParams.get('itemId') || '';
  const prefillRequisitionId = searchParams.get('requisitionId') || ''; // B4-F: "Raise PO" from an approved requisition
  const toast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [catalogueMap, setCatalogueMap] = useState<Map<string, SupplierItem>>(new Map());
  // 11.2: optional link to a buyer/export order. Only garments tenants with the merchandising
  // module enabled get any rows here (the list endpoint 403s otherwise, caught below), so the
  // picker stays hidden for everyone else.
  const [buyerOrders, setBuyerOrders] = useState<IBuyerOrder[]>([]);
  const [form, setForm] = useState({
    supplierId: prefillSupplierId,
    orderDate: toLocalIsoDate(),
    expectedDate: '',
    notes: '',
    taxAmount: 0,
    buyerOrderId: '',
    requisitionId: '',
  });
  // B4-F: approved requisitions with quantity still to order (needs the requisition view permission: silent when refused)
  const [requisitions, setRequisitions] = useState<RaisableRequisition[]>([]);
  const [requisitionNote, setRequisitionNote] = useState('');
  const [pickedRequisition, setPickedRequisition] = useState<RaisableRequisition | null>(null);
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [itemPrefillApplied, setItemPrefillApplied] = useState(false);
  const [suggestedPrices, setSuggestedPrices] = useState<Map<string, number>>(new Map()); // Polish 14: standard cost per requisition item
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // ---- G5a: document currency (indicative PO rate; the GRN fixes the booking rate) and the optional B2B LC link ----
  const [currency, setCurrency] = useState('BDT');
  const [fxRate, setFxRate] = useState<number | ''>('');
  const foreign = isForeignCurrency(currency);
  const [b2bLcs, setB2bLcs] = useState<BackToBackLc[]>([]);
  const [backToBackLcId, setBackToBackLcId] = useState('');
  const institutionIdForFc = AuthService.getSelectedInstitution() ?? '';
  // ---- end G5a ----

  useEffect(() => {
    Promise.all([
      loadSuppliersSafely(() => SupplierService.listByInstitution(0, 500)),
      ItemService.getItemsByInstitution(0, 500, '', 'STOCK').then(r => r.content), // FW-2A
    ]).then(([sups, its]) => { setSuppliers(sups.suppliers); setItems(its); if (sups.error) toast.showError(sups.error); })
      .catch(e => toast.showApiError(e, 'Failed to load data'))
      .finally(() => setLoading(false));

    // 11.2: fetched separately with a silent catch — a non-garments tenant (merchandising module
    // off) gets a 403 here, which must NOT block the whole PO form; it just means no picker.
    const institutionId = AuthService.getSelectedInstitution();
    if (institutionId) {
      BuyerOrderService.list(institutionId, 0, 200)
        .then(r => setBuyerOrders(r.content))
        .catch(() => setBuyerOrders([]));
      PoApprovalService.raisableRequisitions(institutionId)
        .then(rows => setRequisitions(rows ?? []))
        .catch(() => setRequisitions([]));
      // G5a: silent like the buyer orders: no LC module / no LC_VIEW just means no picker
      BackToBackLcService.listByInstitution(institutionId, { page: 0, size: 200 })
        .then(r => setB2bLcs((r.content ?? []).filter(lc => lc.status !== 'CLOSED')))
        .catch(() => setB2bLcs([]));
    }
    if (prefillRequisitionId) applyRequisition(prefillRequisitionId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- B4-F: pre-fill the form from the unordered lines of an approved requisition ----
  const applyRequisition = async (requisitionId: string) => {
    if (!requisitionId) {
      setForm(f => ({ ...f, requisitionId: '' }));
      setRequisitionNote('');
      setPickedRequisition(null);
      return;
    }
    try {
      const pre = await PoApprovalService.prefill(requisitionId);
      if (pre.lines.length === 0) {
        // Polish 15: an exhausted requisition is an answer, not an error
        setForm(f => ({ ...f, requisitionId: '' }));
        setPickedRequisition(null);
        setRequisitionNote('');
        toast.showError(pre.message ?? `Requisition ${pre.refNo} has nothing left to order`);
        return;
      }
      setForm(f => ({ ...f, requisitionId, buyerOrderId: pre.buyerOrderId ?? f.buyerOrderId }));
      setPickedRequisition({
        id: requisitionId, refNo: pre.refNo, date: pre.date, remarks: pre.remarks, buyerOrderId: pre.buyerOrderId,
        linesToOrder: pre.lines.length, quantityToOrder: pre.lines.reduce((n, l) => n + l.remainingQty, 0),
      });
      setRequisitionNote(`Requisition ${pre.refNo}: the lines below are the quantity still to order.`);
      setSuggestedPrices(new Map(pre.lines.filter(l => (l.suggestedUnitPrice ?? 0) > 0).map(l => [l.itemId, l.suggestedUnitPrice as number])));
      setLines(pre.lines.map(l => ({
        itemId: l.itemId, quantity: l.remainingQty, unitPrice: l.suggestedUnitPrice ?? 0, isFromCatalogue: false,   // Polish 14: standard cost until a catalogue price is known
      })));
    } catch (e) {
      toast.showApiError(e, 'Failed to load the requisition');
    }
  };

  // Prices for requisition lines come from the chosen supplier's catalogue once it is known.
  useEffect(() => {
    if (!form.requisitionId || catalogueMap.size === 0) return;
    setLines(ls => ls.map(l => {
      const cat = catalogueMap.get(l.itemId);
      // a catalogue price replaces the placeholder (0 or the standard-cost suggestion), never a price the user typed
      const untouched = l.unitPrice === 0 || l.unitPrice === suggestedPrices.get(l.itemId);
      return cat && untouched && !foreign ? { ...l, unitPrice: cat.unitPrice, isFromCatalogue: true } : l;
    }));
  }, [catalogueMap, form.requisitionId]); // eslint-disable-line react-hooks/exhaustive-deps
  // ---- end B4-F ----

  // When supplier changes, fetch catalogue
  useEffect(() => {
    if (!form.supplierId) { setCatalogueMap(new Map()); return; }
    SupplierItemService.listBySupplier(form.supplierId)
      .then(rows => {
        const map = new Map(rows.map(r => [r.itemId, r]));
        setCatalogueMap(map);
        if (prefillItemId && !itemPrefillApplied) {
          const cat = map.get(prefillItemId);
          setLines([{
            itemId: prefillItemId,
            quantity: 1,
            unitPrice: cat ? cat.unitPrice : 0,
            isFromCatalogue: !!cat,
          }]);
          setItemPrefillApplied(true);
        }
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier catalogue'));
  }, [form.supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = useMemo(() => lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0), [lines]);
  const total = subtotal + (foreign ? 0 : form.taxAmount);
  // G5a: the B2B LCs this PO can link to: the chosen supplier's, not closed
  const supplierLcs = useMemo(() => b2bLcs.filter(lc => !lc.supplierId || lc.supplierId === form.supplierId), [b2bLcs, form.supplierId]);
  useEffect(() => {
    if (backToBackLcId && !supplierLcs.some(lc => lc.id === backToBackLcId)) setBackToBackLcId('');
  }, [supplierLcs]); // eslint-disable-line react-hooks/exhaustive-deps

  // G5a: catalogue and standard-cost prices are BDT: switching between BDT and a foreign currency clears them
  const onCurrencyChange = (c: string, r: number | '') => {
    if (isForeignCurrency(c) !== foreign) {
      setLines(ls => ls.map(l => ({ ...l, unitPrice: 0, isFromCatalogue: false })));
      if (isForeignCurrency(c)) setForm(f => ({ ...f, taxAmount: 0 }));
    }
    setCurrency(c);
    setFxRate(r);
  };

  const updateLine = (idx: number, patch: Partial<LineDraft>) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const onItemPick = (idx: number, itemId: string) => {
    const cat = foreign ? undefined : catalogueMap.get(itemId);   // G5a: the catalogue price is BDT
    if (cat) updateLine(idx, { itemId, unitPrice: cat.unitPrice, isFromCatalogue: true });
    else updateLine(idx, { itemId, isFromCatalogue: false });
  };

  const onPriceEdit = (idx: number, val: number) => updateLine(idx, { unitPrice: val, isFromCatalogue: false });

  const handleSubmit = async () => {
    if (!form.supplierId) { toast.showApiError(null, 'Pick a supplier'); return; }
    if (lines.some(l => !l.itemId || l.quantity <= 0 || l.unitPrice <= 0)) {
      toast.showApiError(null, 'Every line needs an item, quantity, and price'); return;
    }
    if (foreign && !(Number(fxRate) > 0)) { toast.showApiError(null, `Enter the ${currency} rate`); return; }
    const dto: PurchaseOrderCreateDTO = {
      supplierId: form.supplierId,
      orderDate: form.orderDate,
      expectedDate: form.expectedDate || undefined,
      notes: form.notes || undefined,
      buyerOrderId: form.buyerOrderId || undefined,
      requisitionId: form.requisitionId || undefined,
      taxAmount: foreign ? 0 : form.taxAmount,
      institutionId: AuthService.getSelectedInstitution()!!,
      lines: lines.map(l => ({ itemId: l.itemId, quantity: l.quantity, unitPrice: l.unitPrice })),
      ...(foreign ? { currencyCode: currency, exchangeRate: Number(fxRate) } : {}),
      ...(backToBackLcId ? { backToBackLcId } : {}),
    };
    try {
      setSaving(true);
      const po = await PurchaseOrderService.create(dto);
      toastSaved(toast, 'Purchase order created', po.warnings);
      navigate(`/procurement/purchase-orders/${po.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100 }} data-help-page="procurement.poForm.page">
      <Typography variant="h5" sx={{ mb: 2 }}>New Purchase Order</Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }} data-help-section="procurement.poForm.section.header">
        <SearchableSelect<Supplier>
          data-help="procurement.poForm.field.supplier"
          options={suppliers}
          value={form.supplierId}
          onChange={id => setForm({ ...form, supplierId: id })}
          getOptionLabel={s => s.name}
          label="Supplier"
          required
          size="medium"
        />
        <TextField
          data-help="procurement.poForm.field.orderDate"
          label="Order Date"
          type="date"
          value={form.orderDate}
          onChange={e => setForm({ ...form, orderDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          data-help="procurement.poForm.field.expectedDate"
          label="Expected Date"
          type="date"
          value={form.expectedDate}
          onChange={e => setForm({ ...form, expectedDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          data-help="procurement.poForm.field.tax"
          label="Tax Amount"
          type="number"
          value={foreign ? 0 : form.taxAmount}
          disabled={foreign}
          helperText={foreign ? 'No tax on a foreign-currency PO: import VAT, AT and AIT come through the bill of entry' : undefined}
          onChange={e => setForm({ ...form, taxAmount: Number(e.target.value) })}
          inputProps={{ 'aria-label': 'Tax Amount' }}
        />
        {/* ---- G5a ---- */}
        <Box sx={{ gridColumn: '1 / -1' }} data-help-section="procurement.poForm.section.currency">
          <DocumentCurrencyFields institutionId={institutionIdForFc} currency={currency} rate={fxRate}
            asOf={form.orderDate} onChange={onCurrencyChange} />
          {foreign && (
            <Typography variant="caption" color="text.secondary">
              Unit prices are in {currency}. This rate is indicative: each goods receipt is booked at its own rate.
            </Typography>
          )}
        </Box>
        {supplierLcs.length > 0 && (
          <SearchableSelect<BackToBackLc>
            data-help="procurement.poForm.field.b2bLc"
            options={supplierLcs}
            value={backToBackLcId}
            onChange={id => setBackToBackLcId(id)}
            getOptionLabel={lc => `${lc.b2bLcNumber} · ${lc.currencyCode} ${lc.remainingHeadroom} headroom`}
            label="B2B LC (optional)"
            placeholder="None"
            size="medium"
            helperText="A PO under a B2B LC is settled through the LC acceptance: it cannot be billed as a supplier bill"
            sx={{ gridColumn: '1 / -1' }}
          />
        )}
        {/* ---- end G5a ---- */}
        {/* ---- B4-F ---- */}
        {(requisitions.length > 0 || form.requisitionId) && (
          <SearchableSelect<RaisableRequisition>
            data-help="procurement.poForm.requisition"
            options={requisitions}
            value={form.requisitionId}
            onChange={id => applyRequisition(id)}
            selectedOption={pickedRequisition}
            getOptionLabel={r => `${r.refNo} · ${r.linesToOrder} line(s), ${r.quantityToOrder} to order${r.remarks ? ` · ${r.remarks}` : ''}`}
            label="Requisition (optional)"
            placeholder="None"
            size="medium"
            helperText={requisitionNote || 'Raise this PO from an approved requisition: its unordered lines are filled in'}
            sx={{ gridColumn: '1 / -1' }}
          />
        )}
        {/* ---- end B4-F ---- */}
        {buyerOrders.length > 0 && (
          <SearchableSelect<IBuyerOrder>
            data-help="procurement.poForm.field.buyerOrder"
            options={buyerOrders}
            value={form.buyerOrderId}
            onChange={id => setForm({ ...form, buyerOrderId: id })}
            getOptionLabel={bo => `${bo.orderNumber}${bo.buyerPoNumber ? ` · ${bo.buyerPoNumber}` : ''}`}
            label="Buyer order (optional)"
            placeholder="None"
            size="medium"
            helperText="Link this PO to the export order it procures materials for"
            sx={{ gridColumn: '1 / -1' }}
          />
        )}
        <TextField
          sx={{ gridColumn: '1 / -1' }}
          data-help="procurement.poForm.field.notes"
          label="Notes"
          multiline
          rows={2}
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} data-help-section="procurement.poForm.lines">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {lines.map((l, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <SearchableSelect<Item>
                  options={items}
                  value={l.itemId}
                  onChange={id => onItemPick(idx, id)}
                  getOptionLabel={i => i.itemName}
                  label="Item"
                  data-help="procurement.poForm.field.lineItem"
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <TextField
                    type="number"
                    size="small"
                    label="Quantity"
                    data-help="procurement.poForm.field.lineQty"
                    value={l.quantity}
                    onChange={e => updateLine(idx, { quantity: Number(e.target.value) })}
                    inputProps={{ 'aria-label': 'Quantity' }}
                    sx={{ flex: '1 1 120px', maxWidth: 200 }}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label={foreign ? `Unit Price (${currency})` : 'Unit Price'}
                    data-help="procurement.poForm.field.linePrice"
                    value={l.unitPrice}
                    onChange={e => onPriceEdit(idx, Number(e.target.value))}
                    inputProps={{ 'aria-label': 'Unit Price' }}
                    sx={{ flex: '1 1 120px', maxWidth: 200 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    Line total: <strong>{foreign ? formatFc(currency, l.quantity * l.unitPrice) : formatMoney(l.quantity * l.unitPrice)}</strong>
                    {l.isFromCatalogue && <Chip label="from catalogue" size="small" sx={{ ml: 1 }} />}
                  </Typography>
                  <IconButton data-help="procurement.poForm.action.remove" size="small" aria-label="Remove line" onClick={() => setLines(ls => ls.filter((_, k) => k !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
        <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.poLine.col.item">Item</TableCell>
                  <TableCell data-help="procurement.poLine.col.qty">Quantity</TableCell>
                  <TableCell data-help="procurement.poLine.col.price">Unit Price{foreign ? ` (${currency})` : ''}</TableCell>
                  <TableCell data-help="procurement.poLine.col.total">Line Total{foreign ? ` (${currency})` : ''}</TableCell>
                  <TableCell data-help="procurement.poForm.col.remove"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <SearchableSelect<Item>
                        options={items}
                        value={l.itemId}
                        onChange={id => onItemPick(idx, id)}
                        getOptionLabel={i => i.itemName}
                        label="Item"
                  data-help="procurement.poForm.field.lineItem"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        label="Quantity"
                    data-help="procurement.poForm.field.lineQty"
                        value={l.quantity}
                        onChange={e => updateLine(idx, { quantity: Number(e.target.value) })}
                        inputProps={{ 'aria-label': 'Quantity' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          type="number"
                          size="small"
                          label={foreign ? `Unit Price (${currency})` : 'Unit Price'}
                          data-help="procurement.poForm.field.linePrice"
                          value={l.unitPrice}
                          onChange={e => onPriceEdit(idx, Number(e.target.value))}
                          inputProps={{ 'aria-label': 'Unit Price' }}
                        />
                        {l.isFromCatalogue && <Chip label="from catalogue" size="small" />}
                      </Box>
                    </TableCell>
                    <TableCell>{foreign ? formatFc(currency, l.quantity * l.unitPrice) : formatMoney(l.quantity * l.unitPrice)}</TableCell>
                    <TableCell>
                      <IconButton data-help="procurement.poForm.action.remove" size="small" onClick={() => setLines(ls => ls.filter((_, i) => i !== idx))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </ScrollableTable>
        )}
        <Button data-help="procurement.poForm.addLine" startIcon={<AddIcon />} onClick={() => setLines(ls => [...ls, emptyLine()])}>Add line</Button>
      </Paper>

      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }} data-help-section="procurement.poForm.section.totals">
        {foreign ? (
          <>
            <Typography variant="h6">Total: {formatFc(currency, subtotal)}</Typography>
            {Number(fxRate) > 0 && (
              <Typography variant="body2" color="text.secondary">≈ {formatMoney(subtotal * Number(fxRate))} at the indicative rate {fxRate}</Typography>
            )}
          </>
        ) : (
          <>
            <Typography>Subtotal: {formatMoney(subtotal)}</Typography>
            <Typography>Tax: {formatMoney(form.taxAmount)}</Typography>
            <Typography variant="h6">Total: {formatMoney(total)}</Typography>
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button data-help="procurement.poForm.cancel" onClick={() => navigate(-1)}>Cancel</Button>
        <Button data-help="procurement.poForm.create" variant="contained" onClick={handleSubmit} disabled={saving}>Create PO</Button>
      </Box>
    </Box>
  );
};

export default PurchaseOrderCreate;
